import { Button } from "@sellar-io/components";
import { capitalize } from "@sellar-io/common";
import { useCallback, useEffect, useState } from "react";
import { useMatch, useNavigate, useSearchParams } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { Icon } from "../../../components";
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from "../../../components/listbox/Listbox";
import { SharePricingDialog } from "../../../components/menu/dialogs/SharePricingDialog/SharePricingDialog";
import { useMenuDrawerContext } from "../../../components/menu/MenuDrawerContextProvider.shared";
import {
  NavigationBar,
  NavigationBarSubHeader,
} from "../../../components/navigation/NavigationBar";
import { QuantityIndicator } from "../../../components/quantityIndicator/QuantityIndicator";
import * as ROUTES from "../../../constants/routes";
import { BUSINESS_SETTINGS, LOGIN, SUPPLIERS } from "../../../constants/routes";
import {
  SellarContextActionEnum,
  useBasketContext,
  useSellarContext,
  useSellarUser,
} from "../../../hooks";
import { usePriceTierList } from "../../../hooks/api/v2/priceTier/usePriceTierList";
import {
  useHasParentNavigation,
  useParentNavigation,
} from "../../../hooks/useParentNavigation";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useStorefrontLinkSearchStore } from "../../storefrontLinks/StorefrontLinkSearchStore";
import { useStorefrontFilterStore } from "../StorefrontFilterStore";
import { useStockListContext } from "../useStockListContext";
import { StorefrontLinkExitDialog } from "./Storefront/StorefrontDialogs/StorefrontLinkExitDialog";
import { useSignupContext } from "../../../hooks/useSignupContext.shared";
import { generateSignupContinueUrl } from "../../../utils/sellarHelpers";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { useAvailabilityTierList } from "../../../hooks/api/v2/availabilityTier/useAvailabilityTierList";
import { useProductCatalogueMode } from "../../../hooks/useProductCatalogueMode";

export type SelectedFulfillmentType = "direct" | "non-direct";

const FulfillmentTypeSelect = ({
  selectedFulfillmentType = "direct",
  onFulfillmentTypeOptionSelected,
}: {
  selectedFulfillmentType?: SelectedFulfillmentType;
  onFulfillmentTypeOptionSelected: (
    _deliveryType: SelectedFulfillmentType,
  ) => void;
}) => {
  useEffect(() => {
    // If previewing storefront we want to actually set the default to 'direct'
    // without impacting other components by setting the context state by default
    // so leaving it 'undefined' and setting it here
    if (selectedFulfillmentType === "direct")
      onFulfillmentTypeOptionSelected("direct");
  }, [onFulfillmentTypeOptionSelected, selectedFulfillmentType]);
  return (
    <Listbox
      value={selectedFulfillmentType}
      onChange={(deliveryType) => {
        onFulfillmentTypeOptionSelected(
          deliveryType as SelectedFulfillmentType,
        );
      }}
    >
      <ListboxButton className={"bg-white"}>
        Previewing as: {capitalize(selectedFulfillmentType)}
      </ListboxButton>
      <ListboxOptions>
        <ListboxOption value={"direct"}>Direct</ListboxOption>
        <ListboxOption value={"non-direct"}>Non-direct</ListboxOption>
      </ListboxOptions>
    </Listbox>
  );
};

export const CategoryPageNavigationBar = ({
  setPostcodeDialogActive,
}: {
  setPostcodeDialogActive: (_postcodeDialogActive: boolean) => void;
}) => {
  const {
    supplierQuery,
    productApi,
    unAuthorisedUserPostcode,
    connection,
    setSelectedFulfillmentType,
    selectedFulfillmentType,
  } = useStockListContext();
  const { user } = useSellarUser();
  const [_, dispatch] = useSellarContext();
  const {
    setSelectedPriceTier,
    selectedPriceTier,
    setSelectedAvailabilityTier,
    selectedAvailabilityTier,
  } = useStorefrontFilterStore(
    useShallow((state) => ({
      setSelectedPriceTier: state.setSelectedPriceTier,
      selectedPriceTier: state.selectedPriceTier,
      setSelectedAvailabilityTier: state.setSelectedAvailabilityTier,
      selectedAvailabilityTier: state.selectedAvailabilityTier,
    })),
  );

  usePrefillAvailabilityAndPriceTierFromParams();

  const { setIsOnScreen } = useMenuDrawerContext();
  const [sharePricingDialogActive, setSharePricingDialogActive] =
    useState<boolean>(false);
  const [exitPriceTierStorefrontActive, setExitPriceTierStorefrontActive] =
    useState<boolean>(false);
  const { business } = useSellarCurrentBusiness();
  const categoryId = useMatch(ROUTES.CATEGORY_BUYER.pattern)?.params.categoryId;
  const navigate = useNavigate();
  const [{ order }] = useBasketContext();
  const hasParentNavigation = useHasParentNavigation();
  const [searchParams] = useSearchParams();
  const storefrontLink =
    searchParams.get("storefrontLink") || searchParams.get("priceTierLink");
  const { setShowLoginTitle, setSignupDialogActive } = useSignupContext();

  const isLoggedIn = !!user;
  const productCatalogueMode = useProductCatalogueMode();

  const priceTiersQuery = usePriceTierList(
    {},
    {
      enabled:
        business?.type === "supplier" &&
        supplierQuery.data &&
        supplierQuery.data.id === business.Supplier?.id,
    },
  );

  const selectedPriceTierName = priceTiersQuery.data?.pages[0]?.find(
    (priceTier) => priceTier.id === selectedPriceTier,
  )?.name;

  const availabilityTiersQuery = useAvailabilityTierList(
    {},
    {
      enabled:
        business?.type === "supplier" &&
        supplierQuery.data &&
        supplierQuery.data.id === business.Supplier?.id,
    },
  );

  const selectedAvailabilityTierName =
    availabilityTiersQuery.data?.pages[0]?.find(
      (availabilityTier) => availabilityTier.id === selectedAvailabilityTier,
    )?.name;

  const defaultAvailabilityTier = availabilityTiersQuery.data?.pages[0]?.find(
    (availabilityTier) => availabilityTier.defaultType === "default",
  );

  useEffect(() => {
    if (!productCatalogueMode.isBasicMode || !defaultAvailabilityTier) return;

    setSelectedAvailabilityTier(defaultAvailabilityTier.id);
  }, [
    defaultAvailabilityTier,
    productCatalogueMode.isBasicMode,
    setSelectedAvailabilityTier,
  ]);

  const navigateFromDirectEntry = useCallback(() => {
    if (!user) {
      if (unAuthorisedUserPostcode) {
        setPostcodeDialogActive(true);
      }
      setIsOnScreen(true);
    } else if (!business) {
      navigate(LOGIN);
    } else if (business.Supplier) {
      navigate(BUSINESS_SETTINGS);
    } else {
      navigate(SUPPLIERS);
    }
  }, [
    business,
    navigate,
    setIsOnScreen,
    setPostcodeDialogActive,
    unAuthorisedUserPostcode,
    user,
  ]);

  const navigateToParent = useParentNavigation({
    fallback: navigateFromDirectEntry,
  });

  const onBackNavigationClick = (isExitingStorefrontLink: boolean) => {
    if (!connection && storefrontLink && !isExitingStorefrontLink && user) {
      return setExitPriceTierStorefrontActive(true);
    }

    navigateToParent();
  };

  const getLeftIcon = () => {
    if (user) {
      if (storefrontLink) {
        return "CLOSE";
      }
      return "CHEVRON_LEFT";
    }

    if (hasParentNavigation) {
      return "CHEVRON_LEFT";
    }
    if (unAuthorisedUserPostcode) return "MAP_MARKER";

    return "MENU";
  };

  const [, setOnboardingContinueUrl] = useLocalStorage(
    "onboardingContinueUrl",
    "",
  );

  const login = () => {
    const continueSearchParams = generateSignupContinueUrl(
      supplierQuery.data?.id,
      storefrontLink ? `storefrontLink=${storefrontLink}` : "",
    );
    setOnboardingContinueUrl(
      `${continueSearchParams.get("continue")}${
        continueSearchParams.get("queryParameters")
          ? `?${continueSearchParams.get("queryParameters")}`
          : ""
      }`,
    );

    setShowLoginTitle(true);
    setSignupDialogActive(true);
  };

  return (
    <>
      <NavigationBar
        borderBottom={false}
        leftIcon={getLeftIcon()}
        leftIconText={
          !user && unAuthorisedUserPostcode && !hasParentNavigation
            ? unAuthorisedUserPostcode
            : ""
        }
        rightComponent={
          order?.OrderItems?.length ? (
            <div
              className={"flex cursor-pointer items-center"}
              onClick={() => {
                return dispatch({
                  type: SellarContextActionEnum.CHANGE_SIDE_PANEL_ACTIVE,
                  sidePanelActive: true,
                });
              }}
            >
              <Icon icon={"CART"} />
              <QuantityIndicator
                quantity={order.OrderItems.map((item) => item.quantity).reduce(
                  (a, b) => a + b,
                  0,
                )}
              />
            </div>
          ) : (
            !isLoggedIn && (
              <Button color="transparent" size="small" onClick={login}>
                <Icon icon="LOGIN_VARIANT" />
                <span className="hidden sm:block">Log in</span>
              </Button>
            )
          )
        }
        onClickLeftIcon={() => onBackNavigationClick(false)}
        title={
          categoryId
            ? (productApi.data?.name ?? "Loading...")
            : (supplierQuery.data?.Business.name ?? "Loading...")
        }
      />
      {business?.Supplier?.id === supplierQuery.data?.id &&
        priceTiersQuery.data?.pages[0] &&
        !storefrontLink && (
          <NavigationBarSubHeader>
            <div className="flex flex-wrap justify-center gap-2 py-4">
              <Listbox
                value={selectedPriceTier.toString()}
                onChange={(e) => {
                  if (e === "") return setSelectedPriceTier(0);
                  const priceTierId = parseInt(e, 10);
                  setSelectedPriceTier(priceTierId);
                }}
              >
                <ListboxButton className={"bg-white"}>
                  {selectedPriceTierName
                    ? `Price list: ${selectedPriceTierName}`
                    : "No price list (All products)"}
                </ListboxButton>
                <ListboxOptions>
                  <ListboxOption value="">
                    No price list (All products)
                  </ListboxOption>
                  {priceTiersQuery.data.pages[0]
                    .filter((priceTier) => priceTier.derivationType !== "base")
                    .map((priceTier) => (
                      <ListboxOption key={priceTier.id} value={priceTier.id}>
                        {priceTier.name}
                      </ListboxOption>
                    ))}
                </ListboxOptions>
              </Listbox>
              {!productCatalogueMode.isBasicMode && (
                <Listbox
                  value={selectedAvailabilityTier.toString()}
                  onChange={(e) => {
                    if (e === "") return setSelectedAvailabilityTier(0);
                    const availabilityTierId = parseInt(e, 10);
                    setSelectedAvailabilityTier(availabilityTierId);
                  }}
                >
                  <ListboxButton className={"bg-white"}>
                    {selectedAvailabilityTierName
                      ? `Product catalogue: ${selectedAvailabilityTierName}`
                      : "No product catalogue (All products with pricing)"}
                  </ListboxButton>
                  <ListboxOptions>
                    <ListboxOption value={""}>
                      No product catalogue (All products with pricing)
                    </ListboxOption>
                    {availabilityTiersQuery.data?.pages[0]?.map(
                      (availabilityTier) => (
                        <ListboxOption
                          key={availabilityTier.id}
                          value={availabilityTier.id}
                        >
                          {availabilityTier.name}
                        </ListboxOption>
                      ),
                    )}
                  </ListboxOptions>
                </Listbox>
              )}
              <FulfillmentTypeSelect
                selectedFulfillmentType={selectedFulfillmentType}
                onFulfillmentTypeOptionSelected={(deliveryType) => {
                  setSelectedFulfillmentType(deliveryType);
                }}
              />
              <PreviewLinkButton
                priceTierId={selectedPriceTier}
                availabilityTierId={selectedAvailabilityTier}
              />
            </div>
          </NavigationBarSubHeader>
        )}
      {!!selectedPriceTier && selectedPriceTierName && (
        <SharePricingDialog
          active={sharePricingDialogActive}
          onDismiss={() => setSharePricingDialogActive(false)}
          priceTierId={selectedPriceTier}
          optionalPriceTierName={selectedPriceTierName}
          source={"storefront-preview"}
        />
      )}
      {!!storefrontLink && supplierQuery.data && (
        <StorefrontLinkExitDialog
          active={exitPriceTierStorefrontActive}
          onDismiss={() => setExitPriceTierStorefrontActive(false)}
          supplierName={supplierQuery.data.Business.name}
          onExit={() => onBackNavigationClick(true)}
          supplierId={supplierQuery.data.id}
        />
      )}
    </>
  );
};

const PreviewLinkButton = ({
  priceTierId,
  availabilityTierId,
}: {
  priceTierId: number | null;
  availabilityTierId: number | null;
}) => {
  const navigate = useNavigate();
  const {
    setSelectedPriceTierId,
    setPricingChecked,
    setSelectedAvailabilityTierId,
  } = useStorefrontLinkSearchStore();
  const { setSubMenuIndex } = useMenuDrawerContext();
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const navigateToStorefrontLinks = () => {
    const queryParams = new URLSearchParams();

    if (priceTierId) {
      const state = {
        pricingChecked: true,
        selectedPriceTierId: priceTierId,
        selectedAvailabilityTierId: availabilityTierId,
        selectedAudienceTab: "all",
        selectedProductTab: "all",
      };
      setSelectedPriceTierId(priceTierId);
      setPricingChecked(true);
      if (availabilityTierId) {
        setSelectedAvailabilityTierId(availabilityTierId);
      }
      // No encodeURIComponent — URLSearchParams handles encoding
      const jsonString = JSON.stringify(state);
      queryParams.set("storefrontLinkSearchStore", jsonString);
    }

    const route = isDesktop
      ? ROUTES.STOREFRONT_LINKS_PREVIEW
      : ROUTES.STOREFRONT_LINKS;

    const queryString = queryParams.toString();
    const fullUrl = `${route}${queryString ? `?${queryString}` : ""}`;

    navigate(fullUrl);
    setSubMenuIndex(0);
  };

  return <Button onClick={navigateToStorefrontLinks}>Preview Link</Button>;
};

/**Presets the availability and price tiers based on the query parameters */
const usePrefillAvailabilityAndPriceTierFromParams = () => {
  const [searchParams] = useSearchParams();

  const availabilityTierId = searchParams.get("availabilityTierId")
    ? Number(searchParams.get("availabilityTierId"))
    : 0;
  const priceTierId = searchParams.get("priceTierId")
    ? Number(searchParams.get("priceTierId"))
    : 0;

  const { setSelectedPriceTier, setSelectedAvailabilityTier } =
    useStorefrontFilterStore(
      useShallow((state) => ({
        setSelectedPriceTier: state.setSelectedPriceTier,
        selectedPriceTier: state.selectedPriceTier,
        setSelectedAvailabilityTier: state.setSelectedAvailabilityTier,
        selectedAvailabilityTier: state.selectedAvailabilityTier,
      })),
    );

  useEffect(() => {
    if (availabilityTierId) {
      setSelectedAvailabilityTier(availabilityTierId);
    }
    if (priceTierId) {
      setSelectedPriceTier(priceTierId);
    }
  }, [
    availabilityTierId,
    priceTierId,
    setSelectedAvailabilityTier,
    setSelectedPriceTier,
  ]);

  return null;
};
